import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import api from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SUBNAV = [
  { label: "Histoire", to: "/le-club#histoire" },
  { label: "Projet sportif", to: "/projets/projet-sportif", active: true },
  { label: "Formation", to: "/le-club#formation" },
  { label: "Nos valeurs", to: "/le-club#valeurs" },
];

const mediaUrl = (path) => `${API_BASE}/api/files/${path}`;

function youtubeEmbed(url) {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}
function vimeoEmbed(url) {
  const m = (url || "").match(/vimeo\.com\/(\d+)/);
  return m ? `https://player.vimeo.com/video/${m[1]}` : null;
}

function VideoBlock({ axe, bandeau = false }) {
  if (!axe.video_type) return null;
  const common = bandeau ? "aspect-video w-full" : "aspect-video w-full";
  if (axe.video_type === "file" && axe.video_path) {
    return (
      <div className={`${common} bg-navy`} data-testid={`projet-video-${axe.axe}`}>
        <video controls preload="metadata" className="w-full h-full object-cover" src={mediaUrl(axe.video_path)} />
      </div>
    );
  }
  const embed = axe.video_type === "youtube" ? youtubeEmbed(axe.video_url) : axe.video_type === "vimeo" ? vimeoEmbed(axe.video_url) : null;
  if (embed) {
    return (
      <div className={`${common} bg-navy`} data-testid={`projet-video-${axe.axe}`}>
        <iframe src={embed} title="Vidéo du club" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  return null;
}

function Gallery({ photos, onOpen, axeNum }) {
  if (!photos?.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`projet-gallery-${axeNum}`}>
      {photos.map((p, i) => (
        <button key={p} type="button" onClick={() => onOpen(p)} data-testid={`projet-gallery-${axeNum}-${i}`} className="group overflow-hidden aspect-[4/3] bg-navy">
          <img src={mediaUrl(p)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        </button>
      ))}
    </div>
  );
}

function Lightbox({ photo, onClose }) {
  if (!photo) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="projet-lightbox">
      <div className="absolute inset-0 bg-navy/90" onClick={onClose} />
      <button onClick={onClose} data-testid="projet-lightbox-close" className="absolute top-5 right-5 z-10 p-3 text-white/70 hover:text-rouge transition-colors" aria-label="Fermer">
        <X size={26} />
      </button>
      <img src={mediaUrl(photo)} alt="" className="relative max-w-full max-h-[85vh] object-contain" />
    </div>
  );
}

function AxeText({ axe }) {
  return (
    <div>
      <p className="font-heading text-sm tracking-[0.3em] text-rouge font-semibold mb-3" data-testid={`projet-numero-${axe.axe}`}>{axe.numero}</p>
      <h2 className="font-heading uppercase font-semibold tracking-tighter leading-[0.95] text-navy text-3xl sm:text-5xl" data-testid={`projet-titre-${axe.axe}`}>
        {axe.titre}
      </h2>
      {axe.sous_titre && (
        <p className="mt-4 font-heading uppercase tracking-wider text-sm text-rouge" data-testid={`projet-soustitre-${axe.axe}`}>{axe.sous_titre}</p>
      )}
      <div className="mt-6 space-y-4 text-navy/80 leading-relaxed max-w-xl">
        {axe.texte.split(/\n+/).filter(Boolean).map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
      {axe.bouton_label && axe.bouton_lien && (
        <Link
          to={axe.bouton_lien}
          data-testid={`projet-btn-${axe.axe}`}
          className="group mt-8 inline-flex items-center gap-3 bg-navy text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-rouge transition-colors duration-300"
        >
          {axe.bouton_label}
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
        </Link>
      )}
    </div>
  );
}

function AxePhoto({ axe, onOpen }) {
  if (!axe.photo) return null;
  return (
    <button type="button" onClick={() => onOpen(axe.photo)} data-testid={`projet-photo-${axe.axe}`} className="group block w-full overflow-hidden">
      <img src={mediaUrl(axe.photo)} alt={axe.titre} loading="lazy" className="w-full h-[320px] sm:h-[460px] object-cover transition-transform duration-700 group-hover:scale-105" />
    </button>
  );
}

export default function ProjetSportif() {
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/projet").then((r) => setData(r.data)).catch(() => setData({ axes: [], conclusion: null }));
  }, []);

  const axes = data?.axes || [];
  const conclusion = data?.conclusion;
  const axe = (n) => axes.find((a) => a.axe === n);

  return (
    <>
      <SEO title="Projet sportif" description="Le projet sportif du Club Cycliste Le Boulou : performance, formation, développement du vélo et ancrage territorial." />
      <PageHeader kicker="Le Club" title="Projet sportif" />

      <div className="sticky top-20 z-30 bg-white/90 backdrop-blur-xl border-b border-navy/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto">
          {SUBNAV.map((s) => (
            <Link
              key={s.label}
              to={s.to}
              data-testid={`projet-subnav-${s.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`shrink-0 px-4 py-4 font-heading uppercase tracking-wider text-xs transition-colors duration-300 ${
                s.active ? "text-rouge border-b-2 border-rouge" : "text-navy/70 hover:text-rouge"
              }`}
            >
              {s.label}
            </Link>
          ))}
        </div>
      </div>

      <section className="py-16 sm:py-24" data-testid="projet-intro">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-4xl mb-6" data-testid="projet-intro-titre">
              Construire aujourd'hui le club de demain
            </p>
            <p className="text-navy/80 text-lg sm:text-2xl leading-relaxed max-w-3xl">
              Le projet sportif du Club Cycliste Le Boulou repose sur quatre axes complémentaires.
            </p>
            <p className="mt-5 text-steel text-base sm:text-lg max-w-2xl">
              La performance, la formation, le développement et le territoire sont au cœur de notre ambition pour les
              prochaines années.
            </p>
          </Reveal>
        </div>
      </section>

      {/* AXE 01 — photo → texte */}
      {axe(1) && (
        <section className="py-16 sm:py-24 bg-mist" data-testid="projet-axe-1">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <Reveal><AxePhoto axe={axe(1)} onOpen={setLightbox} /></Reveal>
              <Reveal delay={0.1}><AxeText axe={axe(1)} /></Reveal>
            </div>
            <div className="mt-10 space-y-6">
              <Reveal delay={0.15}><Gallery photos={axe(1).galerie} onOpen={setLightbox} axeNum={1} /></Reveal>
              <Reveal delay={0.2}><VideoBlock axe={axe(1)} /></Reveal>
            </div>
          </div>
        </section>
      )}

      {/* AXE 02 — texte → photo */}
      {axe(2) && (
        <section className="py-16 sm:py-24" data-testid="projet-axe-2">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <Reveal className="order-2 lg:order-1"><AxeText axe={axe(2)} /></Reveal>
              <Reveal delay={0.1} className="order-1 lg:order-2"><AxePhoto axe={axe(2)} onOpen={setLightbox} /></Reveal>
            </div>
            <div className="mt-10 space-y-6">
              <Reveal delay={0.15}><Gallery photos={axe(2).galerie} onOpen={setLightbox} axeNum={2} /></Reveal>
              <Reveal delay={0.2}><VideoBlock axe={axe(2)} /></Reveal>
            </div>
          </div>
        </section>
      )}

      {/* AXE 03 — galerie/vidéo → texte + mini blocs */}
      {axe(3) && (
        <section className="py-16 sm:py-24 bg-navy" data-testid="projet-axe-3">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
            <Reveal><VideoBlock axe={axe(3)} bandeau /></Reveal>
            <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-start ${axe(3).video_type ? "mt-12" : ""}`}>
              <Reveal>
                <div className="space-y-6">
                  <AxePhoto axe={axe(3)} onOpen={setLightbox} />
                  <Gallery photos={axe(3).galerie} onOpen={setLightbox} axeNum={3} />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="[&_h2]:text-white [&_p]:text-white/75">
                  <AxeText axe={axe(3)} />
                </div>
              </Reveal>
            </div>
            {axe(3).mini_blocs?.length > 0 && (
              <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="projet-miniblocs-3">
                {axe(3).mini_blocs.filter((b) => b.titre).map((b, i) => (
                  <Reveal key={i} delay={i * 0.08}>
                    {b.lien ? (
                      <Link to={b.lien} data-testid={`projet-minibloc-3-${i}`} className="group block border border-white/15 hover:border-rouge transition-[border-color] duration-300 h-full">
                        {b.photo && <img src={mediaUrl(b.photo)} alt={b.titre} loading="lazy" className="w-full aspect-[4/3] object-cover" />}
                        <p className="p-5 font-heading uppercase font-semibold tracking-tight text-white text-base group-hover:text-rouge transition-colors duration-300">{b.titre}</p>
                      </Link>
                    ) : (
                      <div data-testid={`projet-minibloc-3-${i}`} className="border border-white/15 h-full">
                        {b.photo && <img src={mediaUrl(b.photo)} alt={b.titre} loading="lazy" className="w-full aspect-[4/3] object-cover" />}
                        <p className="p-5 font-heading uppercase font-semibold tracking-tight text-white text-base">{b.titre}</p>
                      </div>
                    )}
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* AXE 04 — photo → texte */}
      {axe(4) && (
        <section className="py-16 sm:py-24 bg-mist" data-testid="projet-axe-4">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <Reveal><AxePhoto axe={axe(4)} onOpen={setLightbox} /></Reveal>
              <Reveal delay={0.1}><AxeText axe={axe(4)} /></Reveal>
            </div>
            <div className="mt-10 space-y-6">
              <Reveal delay={0.15}><Gallery photos={axe(4).galerie} onOpen={setLightbox} axeNum={4} /></Reveal>
              <Reveal delay={0.2}><VideoBlock axe={axe(4)} /></Reveal>
            </div>
          </div>
        </section>
      )}

      {/* CONCLUSION */}
      {conclusion && (
        <section className="relative bg-navy" data-testid="projet-conclusion">
          {conclusion.photo && (
            <img src={mediaUrl(conclusion.photo)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          )}
          <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 py-24 sm:py-36 text-center">
            <Reveal>
              <h2 className="font-heading uppercase font-semibold tracking-tighter leading-[0.95] text-white text-4xl sm:text-6xl" data-testid="projet-conclusion-titre">
                {conclusion.titre.split(". ").map((part, i) => (
                  <span key={i} className="block">{part.replace(/\.$/, "")}.</span>
                ))}
              </h2>
              <p className="mt-8 text-white/70 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto" data-testid="projet-conclusion-texte">
                {conclusion.texte}
              </p>
            </Reveal>
            <div className="mt-10">
              <Reveal delay={0.15}><VideoBlock axe={{ ...conclusion, axe: 0 }} bandeau /></Reveal>
            </div>
          </div>
        </section>
      )}

      <Lightbox photo={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
